import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Collapse, Spinner, Form } from 'react-bootstrap';
import { useProjects } from '../context/ProjectContext';
import { useUsers } from '../context/UserContext';
import { fetchEngagementDashboard } from '../services/project/engagementService';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

const EngagementDashboard = ({ projectId: contextProjectId }) => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { user, isSuperAdmin } = useUsers();

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];

  // If projectId is passed from context, use it and hide project filter
  const isProjectContext = !!contextProjectId;
  const effectiveProjectId = isProjectContext ? contextProjectId : (isSuperAdmin ? '' : (user?.projectId || ''));

  const [filters, setFilters] = useState({
    projectId: effectiveProjectId,
    from: today,
    to: today,
  });

  const [appliedFilters, setAppliedFilters] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const fetchDashboard = async (filters, forceRefresh = false) => {
    try {
      setIsDashboardLoading(true);
      setDashboardError(null);
      const response = await fetchEngagementDashboard(filters, forceRefresh);
      console.log('API response:', response);
      // Handle both wrapped and unwrapped response formats
      if (response && (response.success === true || response.success === undefined)) {
        const dashboardData = response.dashboard || response.data || response;
        console.log('Dashboard data:', dashboardData);
        setDashboard(dashboardData);
        setDashboardError(null);
      } else {
        setDashboardError(response?.message || 'Failed to load dashboard data');
        setDashboard(null);
      }
    } catch (error) {
      console.error('Error fetching engagement dashboard:', error);
      setDashboardError(error.message || 'Failed to load engagement data');
      setDashboard(null);
    } finally {
      setIsDashboardLoading(false);
    }
  };

  // Auto-apply filters for non-super admin users
  useEffect(() => {
    if (!isSuperAdmin && user?.projectId && appliedFilters === null) {
      const filtersToApply = { ...filters };
      setAppliedFilters(filtersToApply);
      fetchDashboard(filtersToApply);
    }
  }, [isSuperAdmin, user?.projectId, appliedFilters]);

  useEffect(() => {
    // Check localStorage.user.roles for isSuper
    const userStr = localStorage.getItem('user');
    let isSuper = false;
    let projectId = null;
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        if (Array.isArray(userObj.roles)) {
          isSuper = userObj.roles.some(role => role.isSuper === true);
          // fallback: get first projectId if not super
          if (!isSuper && userObj.roles.length > 0) {
            projectId = userObj.roles[0].projectId;
          }
        }
      } catch {}
    }
    if (!isSuper && projectId) {
      navigate(`/projects/${projectId}`);
    }
  }, [navigate]);

  const handleToggleAccordion = (index) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  // Get project options from ProjectContext
  const projectOptions = projects
    .filter(project => !project.deletedAt) // Only show active projects
    .map(project => ({
      value: project.id,
      label: project.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">Engagement Dashboard</h2>

          {/* Filter Bar */}
          <div className="mb-4 p-3 bg-light rounded">
            <Row className="mb-3">
              {/* Date Range Filters */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.from}
                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.to}
                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                  />
                </Form.Group>
              </Col>

              {/* Project Filter - Only show for super admin and not in project context */}
              {isSuperAdmin && !isProjectContext && (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Project</Form.Label>
                    <Select
                      isSearchable
                      options={projectOptions}
                      value={projectOptions.find(option => option.value === filters.projectId) || null}
                      onChange={(selected) => {
                        setFilters({
                          ...filters,
                          projectId: selected ? selected.value : ''
                        });
                      }}
                      placeholder="Select project..."
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <div className="mt-3">
              <Button
                variant="primary"
                disabled={!filters.projectId && !isSuperAdmin ? true : !filters.from || !filters.to}
                onClick={() => {
                  setAppliedFilters(filters);
                  fetchDashboard(filters, true);
                  setExpandedIndex(null);
                  console.log('Applied filters:', filters);
                }}
              >
                Apply Filter
              </Button>
              <Button
                variant="secondary"
                className="ms-2"
                onClick={() => {
                  setFilters({
                    projectId: isSuperAdmin ? '' : (user?.projectId || ''),
                    from: today,
                    to: today,
                  });
                  setAppliedFilters(null);
                  setDashboard(null);
                  setDashboardError(null);
                  setExpandedIndex(null);
                  console.log('Filters cleared');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {isDashboardLoading ? (
        <Row className="justify-content-center">
          <Col xs="auto">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Col>
        </Row>
      ) : dashboardError ? (
        <Row className="justify-content-center">
          <Col xs="auto">
            <div className="text-center text-danger">
              <h5>Error Loading Data</h5>
              <p>{dashboardError}</p>
              <Button variant="outline-primary" onClick={() => appliedFilters && fetchDashboard(appliedFilters)}>
                Try Again
              </Button>
            </div>
          </Col>
        </Row>
      ) : dashboard ? (
        <>
          {/* Summary Statistics */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex gap-3 overflow-auto pb-2">
                {dashboard.total && dashboard.total.map((stat, i) => (
                  <Card
                    key={i}
                    className="text-center flex-shrink-0"
                    style={{
                      minWidth: '120px',
                      borderColor: stat.color,
                      borderWidth: '2px'
                    }}
                  >
                    <Card.Body className="p-3">
                      <div
                        className="display-6 fw-bold mb-1"
                        style={{ color: stat.color }}
                      >
                        {stat.count?.toLocaleString() || 0}
                      </div>
                      <div
                        className="small fw-medium"
                        style={{ color: stat.color }}
                      >
                        {stat.label}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
                {dashboard.total && (
                  <Card
                    className="text-center flex-shrink-0"
                    style={{
                      minWidth: '120px',
                      borderColor: '#007bff',
                      borderWidth: '2px'
                    }}
                  >
                    <Card.Body className="p-3">
                      <div
                        className="display-6 fw-bold mb-1"
                        style={{ color: '#007bff' }}
                      >
                        {dashboard.total.reduce((sum, stat) => sum + (stat.count || 0), 0).toLocaleString()}
                      </div>
                      <div
                        className="small fw-medium"
                        style={{ color: '#007bff' }}
                      >
                        Total
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Col>
          </Row>

          {/* Distributor/Area Accordions */}
          <Row>
            <Col>
              {dashboard.statsByDistributors && dashboard.statsByDistributors.length > 0 ? (
                dashboard.statsByDistributors.map((area, index) => {
                  const distributorTotalStats = area.statsByUser && area.statsByUser.length > 0
                    ? area.statsByUser[0].stats.map((_, i) => {
                        const count = area.statsByUser.reduce((sum, user) => sum + (user.stats[i]?.count || 0), 0);
                        const color = area.statsByUser[0].stats[i]?.color;
                        return { count, color };
                      })
                    : [];

                  const distributorGrandTotal = area.statsByUser
                    ? area.statsByUser.reduce((sum, user) =>
                        sum + user.stats.reduce((s, stat) => s + (stat.count || 0), 0), 0
                      )
                    : 0;

                  return (
                    <Card key={index} className="mb-3">
                      <Card.Header
                        className={`d-flex justify-content-between align-items-center ${
                          expandedIndex === index ? 'bg-light' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleToggleAccordion(index)}
                      >
                        <div className="d-flex align-items-center flex-grow-1">
                          <h5 className="mb-0 me-3">{area.areaName || 'Unknown Area'}</h5>
                          <div className="d-flex gap-3">
                            {distributorTotalStats.map((stat, i) => (
                              <span
                                key={i}
                                className="fw-bold"
                                style={{ color: stat.color, fontSize: '1.1em' }}
                              >
                                {stat.count}
                              </span>
                            ))}
                            <span
                              className="fw-bold ms-2"
                              style={{ color: '#007bff', fontSize: '1.1em' }}
                            >
                              [{distributorGrandTotal}]
                            </span>
                          </div>
                        </div>
                        {expandedIndex === index ? (
                          <i className="bi bi-chevron-up" />
                        ) : (
                          <i className="bi bi-chevron-down" />
                        )}
                      </Card.Header>

                      <Collapse in={expandedIndex === index}>
                        <div>
                          <Card.Body className="p-0">
                            {area.statsByUser && area.statsByUser.map((user, userIdx) => (
                              <div
                                key={userIdx}
                                className="p-2 border-bottom border-light"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  console.log('Selected user:', user.id, user.userName);
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-medium">{user.userName || 'Unknown User'}</span>
                                  <div className="d-flex gap-3 align-items-center">
                                    {user.stats && user.stats.map((stat, i) => (
                                      <span
                                        key={i}
                                        className="fw-bold"
                                        style={{ color: stat.color }}
                                      >
                                        {stat.count || 0}
                                      </span>
                                    ))}
                                    <span
                                      className="fw-bold ms-2"
                                      style={{ color: '#007bff' }}
                                    >
                                      [{user.stats ? user.stats.reduce((sum, stat) => sum + (stat.count || 0), 0) : 0}]
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </Card.Body>
                        </div>
                      </Collapse>
                    </Card>
                  );
                })
              ) : (
                <div className="text-center text-muted py-5">
                  <h5>No distributor data available</h5>
                  <p>Try adjusting your filters to see engagement data by distributor.</p>
                </div>
              )}
            </Col>
          </Row>
        </>
      ) : null}
    </Container>
  );
};

export default EngagementDashboard;
